// src/cockpit4/components/OperationsDigitalTwin.tsx
// ============================================
// 运营数字孪生（工作流中心 P2-功能10）- v2.0 详细改进计划
// 企业运营全景模拟 / 流程仿真预测 / 瓶颈预判 / 策略模拟验证 / 资源优化
// 数据来源：localStorage（xeaos_ops_digital_twin）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Boxes, TrendingUp, Gauge, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const SIM_DATA = [
  { day: "D1", current: 68, optimized: 74 },
  { day: "D5", current: 70, optimized: 79 },
  { day: "D10", current: 71, optimized: 84 },
  { day: "D15", current: 72, optimized: 87 },
  { day: "D20", current: 73, optimized: 90 },
  { day: "D30", current: 74, optimized: 94 },
];

const BOTTLENECKS = [
  { name: "内容生成", utilization: 92, status: "瓶颈" },
  { name: "CRM 跟进", utilization: 68, status: "正常" },
  { name: "财务核算", utilization: 55, status: "空闲" },
  { name: "客服响应", utilization: 85, status: "偏高" },
];

export default function OperationsDigitalTwin() {
  const [simSpeed, setSimSpeed] = useState(1);

  const avgUtil = useMemo(() => BOTTLENECKS.reduce((a, b) => a + b.utilization, 0) / BOTTLENECKS.length, []);
  const bottleneckCount = BOTTLENECKS.filter((b) => b.utilization >= 85).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Boxes size={14} className="text-cyan-400" />
        <h3 className="text-[12px] font-medium text-white">运营数字孪生</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Digital Twin</span>
        <div className="ml-auto flex items-center gap-1 rounded-md border border-white/[0.06] overflow-hidden">
          <button onClick={() => setSimSpeed(1)} className={`px-2 py-1 text-[9px] font-mono ${simSpeed === 1 ? "bg-cyan-500/15 text-cyan-300" : "text-gray-500"}`}>1x</button>
          <button onClick={() => setSimSpeed(2)} className={`px-2 py-1 text-[9px] font-mono ${simSpeed === 2 ? "bg-cyan-500/15 text-cyan-300" : "text-gray-500"}`}>2x</button>
          <button onClick={() => setSimSpeed(4)} className={`px-2 py-1 text-[9px] font-mono ${simSpeed === 4 ? "bg-cyan-500/15 text-cyan-300" : "text-gray-500"}`}>4x</button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="平均资源利用率" value={`${avgUtil.toFixed(0)}%`} color="#38BDF8" />
        <MetricCard label="瓶颈节点" value={`${bottleneckCount} 个`} color="#F87171" />
        <MetricCard label="模拟效率提升" value="+20%" color="#34D399" />
        <MetricCard label="仿真精度" value="94%" color="#A78BFA" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">运营效率仿真 · 当前 vs 优化后（30 天）</span>
          </div>
          <div className="h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={SIM_DATA} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={32} domain={[60, 100]} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} />
                <Line type="monotone" dataKey="current" name="当前效率" stroke="#64748B" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                <Line type="monotone" dataKey="optimized" name="优化后" stroke="#34D399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Gauge size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">流程节点负载仿真</span>
          </div>
          <div className="space-y-2">
            {BOTTLENECKS.map((b) => (
              <div key={b.name} className="flex items-center gap-2">
                <span className="w-16 text-[9px] font-mono text-gray-400">{b.name}</span>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${b.utilization}%` }} transition={{ duration: 0.6 }} style={{ background: b.utilization >= 85 ? "#F87171" : b.utilization >= 75 ? "#FBBF24" : "#34D399" }} />
                </div>
                <span className="text-[9px] font-mono w-8 text-right" style={{ color: b.utilization >= 85 ? "#F87171" : "#94A3B8" }}>{b.utilization}%</span>
              </div>
            ))}
          </div>
          <div className="flex items-start gap-1.5 mt-3 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-[9px] font-mono text-amber-400">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            预判：第 15 天「内容生成」将达 98% 利用率 → 建议提前扩容或接入并行生成
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

